import React, { useState, useCallback } from "react";

import {
  IonItem,
  IonList,
  IonLabel,
  IonTextarea,
  IonButton,
  IonCard,
  IonCardContent,
  IonIcon,
} from "@ionic/react";

import { useHistory } from "react-router-dom";

import { sadOutline } from "ionicons/icons";

import moment from "../../Moment";

import Page from "../../components/Page/Page";
import FieldsLocation from "../../components/FieldsLocation";
import Alert from "../../components/Alert";

import DatePicker from "./components/date";
import CarSelect from "./components/CarSelect";
import TimeSelect from "./components/TimeSelect";

import useVacancySearch from "./useVacancySearch";
import useValidation from "./useValidation";
import { PassageForm } from "./useContext";
import useCreateTicket, { TicketComponent } from "./useCreateTicket";
import useChanging from "./useChanging";

import {
  PassageState,
  PassageForm as PassageFormInterface,
  EventChange,
  PassageFormError,
} from "../../interfaces/passage";

import "./style.css";

const AlertNotVacancy: React.FC<{ date: any; excerpt?: string }> = ({
  date,
  excerpt = "Vagas indisponíveis para essa data",
}) => (
  <div className="alert-no-vacancy">
    <div className="excerpt">
      <p>
        {excerpt} {date}
      </p>
    </div>
    <div className="icon">
      <IonIcon size="large" icon={sadOutline} />
    </div>
  </div>
);

interface SecondCardProps {
  form: PassageFormInterface;
  reload?: string;
  formError: PassageFormError;
  setState: (event: any) => any;
  onIonChange: (event: EventChange) => any;
  onValidation: () => boolean;
  onSubmit: (event: any) => void;
}

const SecondCard: React.FC<SecondCardProps> = ({
  form,
  onIonChange,
  onValidation,
  onSubmit,
  formError,
  setState,
  reload,
}) => {
  const { list: vacancies, loading, error } = useVacancySearch("getVacancies", {
    arrival_location_id: form.arrival_location_id,
    departure_location_id: form.departure_location_id,
    date: form.date,
    reload,
  });

  const onReload = () => {
    setState((prevState: PassageState) => ({
      ...prevState,
      reload: moment().format("YYYY-MM-DD HH:mm:ss"),
    }));
  };

  const validationCar = () => {
    if (vacancies.length === 0 || formError.is_expired) {
      return true;
    }

    if (!((form.hour || parseInt(form.hour as any) === 0) && form.minutes)) {
      return true;
    }
  };
  if (error) {
    return (
      <Page back="/" name="Nova marcação">
        <Alert
          variant="error"
          content={
            <div className="alert-no-vacancy">
              <div className="excerpt">
                <p>Não foi possível estabelecer comunicação com o servidor</p>
                <p>Por favor verifique sua conexão</p>
              </div>
              <div className="icon">
                <IonIcon size="large" icon={sadOutline} />
              </div>
            </div>
          }
        />
      </Page>
    );
  }
  return (
    <IonCard>
      <IonCardContent className="no-padding">
        <IonList>
          <DatePicker
            depature_location_id={form.departure_location_id}
            arrival_location_id={form.arrival_location_id}
            onIonChange={onIonChange}
            date={form.date}
            onReload={onReload}
          />
          <TimeSelect
            vacancies={vacancies}
            formError={formError}
            loading={loading}
            onIonChange={onIonChange}
          />
          <CarSelect
            disabled={validationCar()}
            date={form.date}
            departure_location_id={form.departure_location_id}
            arrival_location_id={form.arrival_location_id}
            hour={form.hour}
            minutes={form.minutes}
            onIonChange={onIonChange}
          />
          <IonItem>
            <IonLabel position="floating">Observação</IonLabel>
            <IonTextarea
              value={form.observation}
              onIonChange={(e) =>
                onIonChange({
                  detail: {
                    name: "observation",
                    value: e.detail.value,
                  },
                })
              }
            />
          </IonItem>
        </IonList>
        {!loading &&
        vacancies.length === 0 &&
        form.departure_location_id !== "0" &&
        form.arrival_location_id !== "0" ? (
          <Alert
            variant="info"
            content={
              <AlertNotVacancy date={moment(form.date).format("DD/MM/YYYY")} />
            }
          />
        ) : null}
        {formError.is_expired ? (
          <Alert
            variant="info"
            content={
              <AlertNotVacancy
                date={moment(form.date).format("DD/MM/YYYY")}
                excerpt="O horário selecionado não está valido"
              />
            }
          />
        ) : null}
        <IonButton
          disabled={onValidation()}
          className="btnFull"
          expand="full"
          onClick={onSubmit}
        >
          Solicitar Passagem
        </IonButton>
      </IonCardContent>
    </IonCard>
  );
};
const InitialState = {
  form: {
    arrival_location_id: "0",
    departure_location_id: "0",
    date: moment().format("YYYY-MM-DD"),
    hour: "",
    minutes: "",
    observation: "",
    car_id: "",
  },
  reload: "",
};

const NewPassage: React.FC = () => {
  const [state, setState] = useState<PassageState>(
    Object.assign({}, InitialState)
  );

  const { push } = useHistory();

  const { onValidation, setFormError, formError } = useValidation(
    state.form.date,
    state.form.hour,
    state.form.minutes
  );

  const { onIonChange } = useChanging({
    setFormError,
    setState,
  });

  const {
    send,
    alert: ticketAlert,
    loading: ticketLoading,
    setAlert,
    setLoading,
  } = useCreateTicket(state.form);

  const onSubmit = useCallback(() => {
    const validation = onValidation();
    if (!validation) {
      send();
    }
  }, [state]);

  const onSuccess = () => {
    setState(() => Object.assign({}, InitialState));
    push("/page/passages");
  };

  return (
    <Page back="/page/passages" name="Nova marcação">
      <PassageForm.Provider value={state.form}>
        <IonCard>
          <IonCardContent className="no-padding">
            <IonList>
              <FieldsLocation form={state.form} onIonChange={onIonChange} />
            </IonList>
          </IonCardContent>
        </IonCard>
        <SecondCard
          reload={state.reload}
          setState={setState}
          form={state.form}
          onIonChange={onIonChange}
          formError={formError}
          onSubmit={onSubmit}
          onValidation={onValidation}
        />
        <TicketComponent
          alert={ticketAlert}
          loading={ticketLoading}
          setAlert={setAlert}
          setLoading={setLoading}
          onSuccess={onSuccess}
        />
      </PassageForm.Provider>
    </Page>
  );
};

export default NewPassage;
